var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Flex, Icon, Text } from "@costar/theme-ui";
var ItemType;
(function (ItemType) {
    ItemType[ItemType["Page"] = 0] = "Page";
    ItemType[ItemType["Ellipses"] = 1] = "Ellipses";
})(ItemType || (ItemType = {}));
var EllipsesSide;
(function (EllipsesSide) {
    EllipsesSide[EllipsesSide["Right"] = 0] = "Right";
    EllipsesSide[EllipsesSide["Left"] = 1] = "Left";
})(EllipsesSide || (EllipsesSide = {}));
var NavType;
(function (NavType) {
    NavType[NavType["Next"] = 0] = "Next";
    NavType[NavType["Previous"] = 1] = "Previous";
})(NavType || (NavType = {}));
var sxPointer = { cursor: "pointer!important" };
var sxNotAllowed = { cursor: "not-allowed!important" };
var PaginationItem = function (props) {
    return (React.createElement(Flex, { color: "blue", fontSize: "sm", fontWeight: 300, height: "auto", onClick: props.onItemClick, sx: props.disabled ? sxNotAllowed : sxPointer, alignItems: "center", justifyContent: "center", "automation-id": "footer-pagination-pagenumber", className: props.navigation ? "footer-pagination-arrow" : "footer-pagination-pagenumber" }, props.children));
};
var Navigation = function (props) {
    var icon = useMemo(function () {
        return props.NavType === NavType.Next ? "chevronRight" : "chevronLeft";
    }, [props.NavType]);
    var color = useMemo(function () {
        return props.Enabled ? "primary" : "neutral.dark";
    }, [props.Enabled]);
    if (props.Enabled) {
        return (React.createElement(PaginationItem, { onItemClick: props.onItemClick, navigation: true },
            React.createElement(Icon, { mx: "xs", icon: icon, color: color, sx: sxPointer })));
    }
    else
        return (React.createElement(PaginationItem, { onItemClick: undefined, navigation: true },
            React.createElement(Icon, { mx: "xs", icon: icon, color: color, sx: sxNotAllowed })));
};
var getTotalPageCount = function (props) {
    var _a = props.total, total = _a === void 0 ? 0 : _a, _b = props.pageSize, pageSize = _b === void 0 ? 1 : _b;
    return Math.ceil(total / pageSize) || 1;
};
var getCurrentPageNumber = function (props) {
    var _a = props.currentPageNumber, currentPageNumber = _a === void 0 ? 1 : _a;
    return currentPageNumber > 0 && currentPageNumber <= getTotalPageCount(props) ? currentPageNumber : 1;
};
var getPage = function (Id, Label) {
    return { Id: Id, Label: Label, ItemType: ItemType.Page };
};
var getPageRange = function (start, end, formatNumber) {
    var pages = [];
    for (var i = start; i <= end; i++) {
        pages.push(getPage(i, formatNumber(i)));
    }
    return pages;
};
var getEllipsesPage = function (eSide) {
    if (eSide === void 0) { eSide = EllipsesSide.Right; }
    return { Label: "...", ItemType: ItemType.Ellipses, EllipsesSide: eSide };
};
export var Pagination = function (props) {
    var currentPageNumber = useMemo(function () {
        return getCurrentPageNumber(props);
    }, [props]);
    var _a = useState(props.currentPageNumber != null && props.currentPageNumber < getTotalPageCount(props)), enableNext = _a[0], setEnableNext = _a[1];
    var _b = useState(currentPageNumber > 1), enablePrevious = _b[0], setEnablePrevious = _b[1];
    useEffect(function () {
        setEnableNext(currentPageNumber < getTotalPageCount(props));
        setEnablePrevious(currentPageNumber > 1);
    }, [props.total, setEnableNext, currentPageNumber, props]);
    var triggerPageChange = useCallback(function (pageNumber) {
        if (props.onPageChange) {
            props.onPageChange(pageNumber);
        }
    }, [props]);
    var onPageClick = useCallback(function (page) {
        var pageNumber = getCurrentPageNumber(props);
        if (page.ItemType === ItemType.Ellipses) {
            if (page.EllipsesSide === EllipsesSide.Left) {
                pageNumber = pageNumber - 2;
            }
            else {
                pageNumber = pageNumber + 2;
            }
            triggerPageChange(pageNumber);
        }
        else if (pageNumber !== page.Id) {
            setEnablePrevious(page.Id > 1);
            setEnableNext(page.Id < getTotalPageCount(props));
            triggerPageChange(page.Id);
        }
    }, [props, triggerPageChange]);
    var next = useCallback(function () {
        if (!enableNext) {
            return;
        }
        var pageNumber = getCurrentPageNumber(props);
        var totalPageCount = getTotalPageCount(props);
        setEnableNext(pageNumber + 1 < totalPageCount);
        setEnablePrevious(pageNumber - 1 > 1);
        if (pageNumber + 1 <= totalPageCount) {
            triggerPageChange(pageNumber + 1);
        }
    }, [props, triggerPageChange, enableNext]);
    var previous = useCallback(function () {
        if (!enablePrevious) {
            return;
        }
        var pageNumber = getCurrentPageNumber(props);
        var totalPageCount = getTotalPageCount(props);
        setEnableNext(pageNumber - 1 < totalPageCount);
        setEnablePrevious(pageNumber - 1 > 1);
        if (pageNumber > 1) {
            triggerPageChange(pageNumber - 1);
        }
    }, [enablePrevious, props, triggerPageChange]);
    var onPageClickHandler = useCallback(function (page) { return function () {
        onPageClick(page);
    }; }, [onPageClick]);
    var calculatePages = useCallback(function () {
        var _a;
        var formatNumber = (_a = props.formatNumber) !== null && _a !== void 0 ? _a : (function (pn) { return pn.toString(); });
        var totalPageCount = getTotalPageCount(props);
        //Place ellipses as per AC
        if (totalPageCount >= 7) {
            var pageNumber = getCurrentPageNumber(props);
            var pages_1 = [];
            if (pageNumber <= 4) {
                pages_1.push.apply(pages_1, getPageRange(1, 6, formatNumber));
                pages_1.push(getEllipsesPage());
                pages_1.push(getPage(totalPageCount, formatNumber(totalPageCount)));
            }
            else if (pageNumber > 4) {
                pages_1.push(getPage(1, "1"));
                pages_1.push(getEllipsesPage(EllipsesSide.Left));
                var second = pageNumber - 2;
                var first = pageNumber - 1;
                pages_1.push(getPage(second, formatNumber(second)));
                pages_1.push(getPage(first, formatNumber(first)));
                pages_1.push(getPage(pageNumber, formatNumber(pageNumber)));
                if (pageNumber + 3 <= totalPageCount) {
                    var third = pageNumber + 1;
                    var forth = pageNumber + 2;
                    pages_1.push(getPage(third, formatNumber(third)));
                    pages_1.push(getPage(forth, formatNumber(forth)));
                    if (pageNumber + 3 === totalPageCount) {
                        pages_1.push(getPage(pageNumber + 3, formatNumber(pageNumber + 3)));
                    }
                    else {
                        pages_1.push(getEllipsesPage());
                        pages_1.push(getPage(totalPageCount, formatNumber(totalPageCount)));
                    }
                }
                else if (pageNumber !== totalPageCount) {
                    var counter = pageNumber + 1;
                    while (counter < totalPageCount) {
                        pages_1.push(getPage(counter, formatNumber(counter)));
                        counter++;
                    }
                    pages_1.push(getPage(totalPageCount, formatNumber(totalPageCount)));
                }
            }
            return pages_1;
        }
        //If total pages are labeled as it is.
        return __spreadArray([], getPageRange(1, totalPageCount, formatNumber), true);
    }, [props]);
    var pages = useMemo(function () {
        var _a;
        var disabledPageColor = props.disablePagination ? "neutral.dark" : "primary";
        return (_a = calculatePages()) === null || _a === void 0 ? void 0 : _a.map(function (page, index) {
            /* tslint:disable-next-line: jsx-no-lambda */
            return (React.createElement(PaginationItem, { key: index, onItemClick: !props.disablePagination ? onPageClickHandler(page) : undefined, disabled: props.disablePagination }, page.ItemType === ItemType.Ellipses ? (React.createElement(Text, { px: "xs", mx: "xs", color: props.disablePagination ? disabledPageColor : "primary" }, "\u2026")) : (React.createElement(Text, { px: "xs", mx: "xs", "automation-id": currentPageNumber === page.Id ? "selected-page" : undefined, className: currentPageNumber === page.Id ? "selected-page" : undefined, fontSize: "sm", fontWeight: 300, color: currentPageNumber === page.Id && !props.disablePagination ? "black" : disabledPageColor }, page.Label))));
        });
    }, [calculatePages, props.disablePagination, currentPageNumber, onPageClickHandler]);
    return (React.createElement(Flex, { flexDirection: "row", justifyContent: "center", flexWrap: "nowrap", textAlign: "center", alignItems: "center", flexGrow: 1, pb: "5px", "automation-id": "footer-pagination" },
        React.createElement(Navigation, { NavType: NavType.Previous, onItemClick: previous, Enabled: enablePrevious && !props.disablePagination }),
        pages,
        React.createElement(Navigation, { NavType: NavType.Next, onItemClick: next, Enabled: enableNext && !props.disablePagination })));
};
export var MemoPagination = React.memo(Pagination);
//# sourceMappingURL=Pagination.js.map